import { useState, useMemo } from "react";
import { api, money, formatApiError, POS_TZ } from "../lib/api";
import { toast } from "sonner";
import CatalogItemPicker from "./CatalogItemPicker";
import {
  PaperPlaneTilt,
  CopySimple,
  Download,
  Plus,
  Trash,
  X,
} from "@phosphor-icons/react";

export const OI_STATUS_COLORS = {
  draft: "bg-zinc-100 text-zinc-600",
  sent: "bg-blue-50 text-blue-700 border-blue-200",
  viewed: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  overdue: "bg-red-50 text-red-700 border-red-200",
  voided: "bg-zinc-100 text-zinc-400 border-zinc-200 line-through",
};

export const OI_STATUS_LABELS = {
  draft: "Draft", sent: "Sent", viewed: "Viewed",
  paid: "Paid", overdue: "Overdue", voided: "Voided",
};

export function copyInvoiceLink(inv) {
  const url = `${window.location.origin}/pay/${inv.payment_token}`;
  navigator.clipboard.writeText(url);
  toast.success("Payment link copied!");
}

export function fmtInvoiceDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      timeZone: POS_TZ, day: "numeric", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}

export default function OnlineInvoiceDetailModal({ invoice, onClose, onUpdated }) {
  const isPaid = invoice.status === "paid";
  const isVoided = invoice.status === "voided";
  // "readOnly" still locks customer info / note / void / delete exactly as
  // before. Line items are unlocked for full editing (description/qty/price/
  // remove) as long as the invoice isn't voided — including items that were
  // already there when it was paid, so a pricing mistake can still be fixed
  // after the fact. The balance simply reopens for whatever the corrected
  // total works out to (see canModifyItems/PATCH below).
  const readOnly = isPaid || isVoided;
  const canModifyItems = !isVoided;
  // Invoices raised from a POS checkout carry ONE rolled-up "Repair · <device>"
  // line, while the detail view lists each repair. Editing showed just that
  // single line, so expand it into the same per-repair breakdown — but only
  // when the breakdown adds up to the same money, so nothing silently shifts.
  const expandForEditing = () => {
    const stored = (invoice.items || []);
    const repairLines = (invoice.devices || []).flatMap((d) =>
      (d.repairs || []).map((r) => ({
        description: r.name,
        quantity: 1,
        unit_price: Number(r.price || 0),
        kind: "repair",
        repair_ticket_id: invoice.linked_ticket_id || null,
        device_label: [d.device_make, d.device_model].filter(Boolean).join(" ") || "",
      }))
    );
    if (repairLines.length < 2) return stored;
    const sum = (list) => list.reduce((s, it) => s + Number(it.unit_price || 0) * Number(it.quantity || 1), 0);
    const names = new Set(repairLines.map((r) => r.description));
    const extras = stored.filter((it) =>
      it.kind !== "repair" && !it.repair_ticket_id && !names.has(it.description)
      && !String(it.description || "").startsWith("Repair · ")
    );
    const expanded = [...repairLines, ...extras];
    if (Math.abs(sum(expanded) - sum(stored)) > 0.05) return stored;
    return expanded;
  };

  const [items, setItems] = useState(
    expandForEditing().map((it, i) => ({ ...it, _key: `${i}` }))
  );
  const [note, setNote] = useState(invoice.note || "");
  const [customerName, setCustomerName] = useState(invoice.customer_name || "");
  const [customerPhone, setCustomerPhone] = useState(invoice.customer_phone || "");
  const [customerEmail, setCustomerEmail] = useState(invoice.customer_email || "");
  const [saving, setSaving] = useState(false);
  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);

  const total = items.reduce((s, it) => s + (it.unit_price || 0) * (it.quantity || 1), 0);
  // Live "already paid" figure (in-store + any completed Stripe payments) so
  // Amount Due updates as soon as items are edited, instead of waiting for
  // the stale amount_due prop from before this edit.
  const alreadyPaid = parseFloat(invoice.in_store_paid || 0) + parseFloat(invoice.stripe_paid_total || 0);
  const liveAmountDue = Math.max(0, total - alreadyPaid);

  // Group items by device_label for multi-device display
  const groupedByDevice = useMemo(() => {
    const groups = [];
    const labelMap = new Map();
    const ungrouped = [];

    for (const it of items) {
      const label = it.device_label;
      if (label) {
        if (!labelMap.has(label)) {
          labelMap.set(label, []);
          groups.push({ label, items: labelMap.get(label) });
        }
        labelMap.get(label).push(it);
      } else {
        ungrouped.push(it);
      }
    }
    return { groups, ungrouped };
  }, [items]);

  const hasMultipleDevices = groupedByDevice.groups.length > 1 ||
    (groupedByDevice.groups.length === 1 && groupedByDevice.ungrouped.length > 0);

  const updateItem = (key, field, value) => {
    setItems((prev) => prev.map((it) => it._key === key ? { ...it, [field]: value } : it));
  };
  const removeItem = (key) => setItems((prev) => prev.filter((it) => it._key !== key));
  const addItem = (deviceLabel = null) => {
    setItems((prev) => [...prev, {
      description: "",
      quantity: 1,
      unit_price: 0,
      device_label: deviceLabel,
      _key: `new-${Date.now()}`
    }]);
  };
  const addCatalogItem = (item) => {
    setItems((prev) => [...prev, { ...item, _key: `new-${Date.now()}` }]);
    setCatalogPickerOpen(false);
    toast.success("Item added — remember to Save");
  };

  const saveAndResend = async (resend) => {
    if (items.length === 0) return toast.error("Add at least one item");
    if (items.some((it) => !it.description.trim())) return toast.error("All items need a description");
    if (!customerName.trim()) return toast.error("Customer name is required");
    setSaving(true);
    try {
      const { data } = await api.patch(`/online-invoices/${invoice.id}`, {
        items: items.map(({ _key, ...rest }) => rest),
        note,
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        customer_email: customerEmail.trim(),
        resend,
      });
      if (data.notification?.errors?.length) {
        data.notification.errors.forEach((e) => toast.error(e));
      }
      toast.success(resend ? "Invoice updated & resent" : "Invoice updated");
      onUpdated();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const voidInvoice = async () => {
    if (!window.confirm(`Void invoice ${invoice.invoice_number}? This cannot be undone.`)) return;
    try {
      await api.post(`/online-invoices/${invoice.id}/void`);
      toast.success(`${invoice.invoice_number} voided`);
      onUpdated();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteInvoice = async () => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_number} permanently?`)) return;
    try {
      await api.delete(`/online-invoices/${invoice.id}`);
      toast.success(`${invoice.invoice_number} deleted`);
      onUpdated();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Render a single item row (shared by grouped and flat views)
  const renderItemRow = (it) => {
    const locked = isVoided;
    return (
    <div key={it._key} className="grid grid-cols-[1fr_60px_90px_32px] gap-2 px-4 py-2 items-center border-b border-slate-50 last:border-0">
      {locked ? (
        <>
          <div className="text-[13px] text-[#121216]">{it.description}</div>
          <div className="text-center text-[13px] text-[#4B5563]">{it.quantity}</div>
          <div className="text-right font-mono text-[13px] font-semibold">${money(it.unit_price * it.quantity)}</div>
          <div />
        </>
      ) : (
        <>
          <input
            value={it.description}
            onChange={(e) => updateItem(it._key, "description", e.target.value)}
            className="h-9 px-2 border border-slate-200 rounded-lg text-[13px] focus:border-[#1E5FEB] focus:outline-none"
            placeholder="Item description"
            data-testid={`oi-item-desc-${it._key}`}
          />
          <input
            type="number"
            min="1"
            value={it.quantity}
            onChange={(e) => updateItem(it._key, "quantity", Math.max(1, parseInt(e.target.value) || 1))}
            className="h-9 px-2 border border-slate-200 rounded-lg text-[13px] text-center focus:border-[#1E5FEB] focus:outline-none"
            data-testid={`oi-item-qty-${it._key}`}
          />
          <input
            type="number"
            min="0"
            step="0.01"
            value={it.unit_price}
            onChange={(e) => updateItem(it._key, "unit_price", parseFloat(e.target.value) || 0)}
            className="money-field h-9 px-2 border border-slate-200 rounded-lg text-[13px] text-right font-mono focus:border-[#1E5FEB] focus:outline-none"
            data-testid={`oi-item-price-${it._key}`}
          />
          <button onClick={() => removeItem(it._key)} className="w-7 h-7 flex items-center justify-center rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`oi-item-remove-${it._key}`}>
            <Trash size={12} weight="bold" />
          </button>
        </>
      )}
    </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/60 flex items-center justify-center p-3" onClick={onClose}>
      <div
        className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-slate-200 overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="oi-detail-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-semibold">Edit Invoice</div>
            <h2 className="font-heading text-xl font-bold text-[#121216]">{invoice.invoice_number}</h2>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center h-7 px-3 rounded-full text-[10px] font-bold uppercase tracking-wider border ${OI_STATUS_COLORS[invoice.status] || OI_STATUS_COLORS.draft}`}>
              {OI_STATUS_LABELS[invoice.status] || invoice.status}
            </span>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500" data-testid="oi-detail-close">
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Customer info */}
          <div className="grid grid-cols-2 gap-4 text-[13px]">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF] font-semibold mb-0.5">Customer</div>
              {readOnly ? (
                <div className="font-semibold text-[#121216]">{invoice.customer_name}</div>
              ) : (
                <input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full h-9 px-2 border border-slate-200 rounded-lg text-[13px] font-semibold focus:border-[#1E5FEB] focus:outline-none"
                  placeholder="Customer name"
                  data-testid="oi-customer-name"
                />
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF] font-semibold mb-0.5">Contact</div>
              {readOnly ? (
                <>
                  <div className="text-[#4B5563]">{invoice.customer_phone || "—"}</div>
                  <div className="text-[#4B5563]">{invoice.customer_email || "—"}</div>
                </>
              ) : (
                <div className="space-y-1.5">
                  <input
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    className="w-full h-9 px-2 border border-slate-200 rounded-lg text-[13px] focus:border-[#1E5FEB] focus:outline-none"
                    placeholder="Phone"
                    data-testid="oi-customer-phone"
                  />
                  <input
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    className="w-full h-9 px-2 border border-slate-200 rounded-lg text-[13px] focus:border-[#1E5FEB] focus:outline-none"
                    placeholder="Email"
                    data-testid="oi-customer-email"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Line items — grouped by device when multi-device */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF] font-semibold">Line Items</div>
              {canModifyItems && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setCatalogPickerOpen(true)}
                    className="flex items-center gap-1 text-[11px] font-semibold text-[#1E5FEB] hover:text-[#1849C8]"
                    data-testid="oi-add-from-catalog"
                  >
                    <Plus size={12} weight="bold" /> Add from catalog
                  </button>
                  {!hasMultipleDevices && (
                    <button onClick={() => addItem()} className="flex items-center gap-1 text-[11px] font-semibold text-[#1E5FEB] hover:text-[#1849C8]" data-testid="oi-add-item">
                      <Plus size={12} weight="bold" /> Add item
                    </button>
                  )}
                </div>
              )}
            </div>

            {hasMultipleDevices ? (
              <div className="space-y-3">
                {groupedByDevice.groups.map((group, gi) => (
                  <div key={gi} className="rounded-2xl border border-slate-200 overflow-hidden" data-testid={`edit-device-group-${gi}`}>
                    <div className="px-4 py-2.5 bg-[#FAFBFC] border-b border-slate-200 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-md bg-slate-200 flex items-center justify-center text-[9px] font-bold text-slate-600">
                          {gi + 1}
                        </div>
                        <span className="text-[12px] font-bold text-[#121216]">{group.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[12px] text-[#9CA3AF]">
                          ${money(group.items.reduce((s, it) => s + (it.unit_price || 0) * (it.quantity || 1), 0))}
                        </span>
                        {canModifyItems && (
                          <button onClick={() => addItem(group.label)} className="flex items-center gap-1 text-[10px] font-semibold text-[#1E5FEB] hover:text-[#1849C8]" data-testid={`oi-add-item-device-${gi}`}>
                            <Plus size={10} weight="bold" /> Add
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-[1fr_60px_90px_32px] gap-2 px-4 py-1.5 text-[9px] uppercase tracking-widest text-[#9CA3AF] font-semibold border-b border-slate-100 bg-slate-50/50">
                      <div>Description</div>
                      <div className="text-center">Qty</div>
                      <div className="text-right">Price</div>
                      <div />
                    </div>
                    {group.items.map(renderItemRow)}
                  </div>
                ))}

                {/* Ungrouped items (accessories, etc.) */}
                {groupedByDevice.ungrouped.length > 0 && (
                  <div className="rounded-2xl border border-slate-200 overflow-hidden" data-testid="edit-ungrouped-items">
                    <div className="px-4 py-2.5 bg-amber-50/50 border-b border-slate-200 flex items-center justify-between">
                      <span className="text-[12px] font-bold text-amber-800">Other Items</span>
                      {canModifyItems && (
                        <button onClick={() => addItem(null)} className="flex items-center gap-1 text-[10px] font-semibold text-[#1E5FEB] hover:text-[#1849C8]" data-testid="oi-add-item-ungrouped">
                          <Plus size={10} weight="bold" /> Add
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-[1fr_60px_90px_32px] gap-2 px-4 py-1.5 text-[9px] uppercase tracking-widest text-[#9CA3AF] font-semibold border-b border-slate-100 bg-slate-50/50">
                      <div>Description</div>
                      <div className="text-center">Qty</div>
                      <div className="text-right">Price</div>
                      <div />
                    </div>
                    {groupedByDevice.ungrouped.map(renderItemRow)}
                  </div>
                )}
              </div>
            ) : (
              /* Single device / flat layout */
              <div className="rounded-2xl border border-slate-200 overflow-hidden">
                <div className="grid grid-cols-[1fr_60px_90px_32px] gap-2 px-4 py-2 text-[10px] uppercase tracking-widest text-[#9CA3AF] font-semibold border-b border-slate-100 bg-slate-50">
                  <div>Description</div>
                  <div className="text-center">Qty</div>
                  <div className="text-right">Price</div>
                  <div />
                </div>
                {items.map(renderItemRow)}
              </div>
            )}
          </div>

          {/* Total */}
          <div className="space-y-1.5">
            {alreadyPaid > 0.005 && (
              <>
                <div className="flex justify-end items-baseline gap-2">
                  <span className="text-[11px] uppercase tracking-widest text-[#9CA3AF] font-semibold">Subtotal</span>
                  <span className="font-display text-lg text-zinc-500 tabular-nums">${money(total)}</span>
                </div>
                <div className="flex justify-end items-baseline gap-2" data-testid="oi-instore-credit">
                  <span className="text-[11px] uppercase tracking-widest text-emerald-600 font-semibold">
                    {parseFloat(invoice.in_store_paid || 0) > 0.005 ? "In-store paid" : "Paid"}
                  </span>
                  <span className="font-display text-lg font-semibold text-emerald-600 tabular-nums">-${money(alreadyPaid)}</span>
                </div>
              </>
            )}
            <div className="flex justify-end items-baseline gap-2">
              <span className="text-[11px] uppercase tracking-widest text-[#9CA3AF] font-semibold">
                {alreadyPaid > 0.005 ? "Amount Due" : "Total"}
              </span>
              <span className="font-display text-2xl font-bold text-[#121216] tabular-nums" data-testid="oi-amount-due">
                ${money(liveAmountDue)}
              </span>
            </div>
            {isPaid && liveAmountDue > 0.005 && (
              <div className="text-[11px] text-amber-600 font-medium text-right" data-testid="oi-reopened-balance-note">
                Adding this item reopens the balance — save to update the invoice.
              </div>
            )}
          </div>

          {/* Note */}
          {!readOnly ? (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF] font-semibold mb-1">Note</div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-[13px] focus:border-[#1E5FEB] focus:outline-none resize-none"
                placeholder="Optional note for the customer…"
                data-testid="oi-note"
              />
            </div>
          ) : invoice.note ? (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF] font-semibold mb-1">Note</div>
              <div className="text-[13px] text-[#4B5563]">{invoice.note}</div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => copyInvoiceLink(invoice)}
              className="h-10 px-4 rounded-full border border-slate-200 text-[12px] font-semibold text-slate-700 hover:border-[#1E5FEB] hover:text-[#1E5FEB] flex items-center gap-1.5 transition-colors"
              data-testid="oi-detail-copy"
            >
              <CopySimple size={13} weight="bold" /> Copy Link
            </button>
            <button
              onClick={() => {
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/online-invoices/${invoice.id}/pdf`;
                const token = localStorage.getItem("pos_token");
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then((r) => r.blob())
                  .then((blob) => {
                    const a = document.createElement("a");
                    a.href = URL.createObjectURL(blob);
                    a.download = `${invoice.invoice_number}.pdf`;
                    a.click();
                  })
                  .catch(() => toast.error("PDF download failed"));
              }}
              className="h-10 px-4 rounded-full border border-slate-200 text-[12px] font-semibold text-slate-700 hover:border-[#1E5FEB] hover:text-[#1E5FEB] flex items-center gap-1.5 transition-colors"
              data-testid="oi-detail-pdf"
            >
              <Download size={13} weight="bold" /> PDF
            </button>
            {!readOnly && (
              <>
                <button
                  onClick={voidInvoice}
                  className="h-10 px-4 rounded-full border border-amber-300 text-[12px] font-semibold text-amber-700 hover:bg-amber-50 transition-colors"
                  data-testid="oi-detail-void"
                >
                  Void
                </button>
                <button
                  onClick={deleteInvoice}
                  className="h-10 px-4 rounded-full border border-red-300 text-[12px] font-semibold text-red-600 hover:bg-red-50 transition-colors"
                  data-testid="oi-detail-delete"
                >
                  Delete
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canModifyItems && (
              <>
                <button
                  onClick={() => saveAndResend(false)}
                  disabled={saving}
                  className="h-10 px-5 rounded-full bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors"
                  data-testid="oi-detail-save"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={() => saveAndResend(true)}
                  disabled={saving}
                  className="h-10 px-5 rounded-full bg-[#1E5FEB] text-white text-[12px] font-semibold hover:bg-[#1849C8] disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                  data-testid="oi-detail-save-resend"
                >
                  <PaperPlaneTilt size={13} weight="bold" /> {saving ? "Sending…" : "Save & Resend"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <CatalogItemPicker
        open={catalogPickerOpen}
        onClose={() => setCatalogPickerOpen(false)}
        onSelect={addCatalogItem}
      />
    </div>
  );
}
